import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    Button,
    Stack,
    CircularProgress,
    Paper,
    Drawer,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Chip,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import {
    Add as AddIcon,
    FileDownloadOutlined as FileDownloadOutlinedIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import { useSearchParams } from "react-router-dom";
import insightsApi from "../../api/insightsApi";
import registerApi from "./registerApi";
import ImpactForm from "./ImpactForm";
import ImpactsTable from "./ImpactsTable";
import ImpactDetailPanel from "./ImpactDetailPanel";
import ActivityDashboard from "./ActivityDashboard";
import SegmentFilters from "./SegmentFilters";
import SegmentForm from "./SegmentForm";
import exportImpactsToExcel from "./exportImpacts";
import KeepScrollPosition from "./KeepScrollPosition";
import { asList, asTaxonomy, EMPTY_TAXONOMY } from "./impactConstants";
import {
    SEGMENT_PARAM_PREFIX,
    asSegments,
    choiceOf,
    countsFor,
    filtersFrom,
    matchesFilters,
    valueOf,
} from "./segments";

const DRAWER_SX = { "& .MuiDrawer-paper": { width: { xs: "100%", sm: 560, md: 640 } } };

// The readable half of a DRF 400 from the segment endpoints: a duplicate title
// comes back as {"title": [...]}, bad choices as {"choices": [...]}. Both are
// specific and worth showing verbatim; anything else is a generic line.
const segmentErrorFrom = (error, intl) => {
    const data = error?.response?.data;
    const first =
        (Array.isArray(data?.title) && data.title[0]) ||
        (Array.isArray(data?.choices) && data.choices[0]) ||
        (typeof data?.detail === "string" && data.detail) ||
        (typeof data?.error === "string" && data.error) ||
        null;
    return (
        first ||
        intl.formatMessage({ id: "impact.segment_save_failed", defaultMessage: "Failed to save the segment" })
    );
};

const REGISTER_VIEW = "register";
const ACTIVITY_VIEW = "activity";
const WORKSPACE_VIEWS = [
    { key: REGISTER_VIEW, labelId: "impact.view_register", label: "Impact register" },
    { key: ACTIVITY_VIEW, labelId: "activity.view_dashboard", label: "Activity dashboard" },
];

// The unified Impacts workspace: the project's SEGMENTS as a filter bar, and one
// table of impacts under it.
//
// Segments replaced the fixed Area -> Group pair. Each is a title the project
// chooses ("Region", "Function", "Seniority") with its own list of choices, as
// many segments as the programme needs; an impact takes at most one choice in
// each. Filters AND together, so the register can be cut by any combination.
//
// Scoped to a PROJECT and mounted as the project workspace's Impacts tab — no
// back link and no page title, the project page owns both. Nothing here is
// wave-scoped: every wave reads the same register.
//
// The view and the filters live in the URL (`?view=`, `?seg.<id>=<choice>`), so
// a view is linkable and back/forward step through it.
//
// `canEdit` is false for a project VIEWER (see utils/projectRoles): everything
// is readable, but every control that adds, changes or deletes is left out.
export default function ImpactsWorkspacePage({ projectId, projectName, canEdit = true }) {
    const intl = useIntl();
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();

    const [impacts, setImpacts] = useState([]);
    const [segments, setSegments] = useState([]);
    const [taxonomy, setTaxonomy] = useState(EMPTY_TAXONOMY);
    const [loading, setLoading] = useState(false);
    // Flips once and stays on: the stale-URL guard has to tell "no segments
    // exist" from "the segments haven't arrived yet".
    const [loaded, setLoaded] = useState(false);
    // The segment list failed to load. Kept apart from the register: a missing
    // segment endpoint must not empty the table, so the impacts still show and
    // only the segment bar steps aside.
    const [segmentsFailed, setSegmentsFailed] = useState(false);

    // ── Drawers and dialogs ──
    const [impactDrawer, setImpactDrawer] = useState(null); // { impact? }
    // `{}` creates a segment, `{ segment }` edits one.
    const [segmentDialog, setSegmentDialog] = useState(null);
    const [segmentSaving, setSegmentSaving] = useState(false);
    const [segmentError, setSegmentError] = useState(null);
    // { impact, tab } — the row whose detail panel is open, and on which tab.
    const [detailDrawer, setDetailDrawer] = useState(null);

    // The tracker rollup, fetched lazily on first open of the dashboard view.
    const [dashboard, setDashboard] = useState(null);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [dashboardError, setDashboardError] = useState(false);

    // ── Confirms ──
    const [pendingImpactDelete, setPendingImpactDelete] = useState(null);
    const [pendingSegmentDelete, setPendingSegmentDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const view = searchParams.get("view") === ACTIVITY_VIEW ? ACTIVITY_VIEW : REGISTER_VIEW;
    // Only filters naming a live segment and choice survive; see filtersFrom.
    const filters = useMemo(() => filtersFrom(searchParams, segments), [searchParams, segments]);

    const fetchAll = useCallback(async () => {
        if (!projectId) return;
        try {
            setLoading(true);
            const [impactData, segmentData, taxonomyData] = await Promise.all([
                registerApi.getImpacts(projectId),
                // Caught on its own, so a failure here costs the filters and
                // nothing else. `null` marks it failed rather than empty.
                registerApi.getSegments(projectId).catch((error) => {
                    console.error("Failed to load the project's segments:", error);
                    return null;
                }),
                insightsApi.getImpactTaxonomy(projectId),
            ]);
            setImpacts(asList(impactData));
            setSegmentsFailed(segmentData === null);
            setSegments(segmentData === null ? [] : asSegments(segmentData));
            setTaxonomy(asTaxonomy(taxonomyData));
        } catch (error) {
            console.error("Failed to load the impacts workspace:", error);
            enqueueSnackbar(
                intl.formatMessage({ id: "impact.failed_to_load_impacts", defaultMessage: "Failed to load impacts" }),
                { variant: "error" }
            );
        } finally {
            setLoading(false);
            setLoaded(true);
        }
    }, [projectId, enqueueSnackbar, intl]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    // ── The tracker rollup ───────────────────────────────────────────────────
    const fetchDashboard = useCallback(async () => {
        if (!projectId) return;
        try {
            setDashboardLoading(true);
            setDashboardError(false);
            setDashboard(await insightsApi.getActivityDashboard(projectId));
        } catch (error) {
            console.error("Failed to load the activity dashboard:", error);
            setDashboardError(true);
        } finally {
            setDashboardLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        if (view === ACTIVITY_VIEW) fetchDashboard();
    }, [view, fetchDashboard]);

    const handleActivitiesChanged = useCallback(() => {
        if (dashboard) fetchDashboard();
    }, [dashboard, fetchDashboard]);

    // A dashboard row names an impact by id; the drawer needs the row itself.
    const openActivitiesFor = useCallback(
        (impactId) => {
            const impact = impacts.find((row) => String(row.id) === String(impactId));
            if (impact) setDetailDrawer({ impact });
        },
        [impacts]
    );

    const visibleImpacts = useMemo(
        () => impacts.filter((impact) => matchesFilters(impact, filters)),
        [impacts, filters]
    );
    const hasFilters = Object.keys(filters).length > 0;

    // ── URL ─────────────────────────────────────────────────────────────────
    // Only non-default state is written, so the plain workspace URL stays clean.
    const writeUrl = useCallback(
        (nextFilters, nextView, options) => {
            const next = {};
            for (const [segmentId, value] of Object.entries(nextFilters)) {
                if (value) next[`${SEGMENT_PARAM_PREFIX}${segmentId}`] = String(value);
            }
            if (nextView !== REGISTER_VIEW) next.view = nextView;
            setSearchParams(next, options);
        },
        [setSearchParams]
    );

    const setFilter = (segmentId, value) => writeUrl({ ...filters, [String(segmentId)]: value }, view);
    const clearFilters = () => writeUrl({}, view);
    const selectView = (nextView) => writeUrl(filters, nextView);

    // A filter on a deleted segment or choice — or an `?area=` / `?group=` from
    // before segments — is dropped from the URL rather than left pointing at
    // nothing. Waits for the first load, when every filter would look stale.
    useEffect(() => {
        if (!loaded || loading) return;
        const live = new Set(Object.keys(filters).map((id) => `${SEGMENT_PARAM_PREFIX}${id}`));
        const stale = [...searchParams.keys()].some(
            (key) => key === "area" || key === "group" || (key.startsWith(SEGMENT_PARAM_PREFIX) && !live.has(key))
        );
        if (stale) writeUrl(filters, view, { replace: true });
    }, [loaded, loading, searchParams, filters, view, writeUrl]);

    // The workbook is the whole register, not the current filter — a download
    // that silently dropped rows would be the more surprising behaviour. The
    // sheet carries an autofilter with a column per segment, so any cut the
    // screen can make, the sheet can too.
    const handleExport = async () => {
        try {
            await exportImpactsToExcel({ impacts, taxonomy, intl, projectName, segments });
        } catch (error) {
            console.error("Failed to export impacts:", error);
            enqueueSnackbar(
                intl.formatMessage({ id: "impact.export_failed", defaultMessage: "Failed to build the export" }),
                { variant: "error" }
            );
        }
    };

    // ── Segments ─────────────────────────────────────────────────────────────
    // The server's 400 goes into the dialog rather than a snackbar: a duplicate
    // title is a correction to what you are still typing.
    const handleSaveSegment = async (payload) => {
        if (!segmentDialog) return;
        const editing = segmentDialog.segment || null;
        try {
            setSegmentSaving(true);
            setSegmentError(null);
            if (editing) await registerApi.updateSegment(projectId, editing.id, payload);
            else await registerApi.createSegment(projectId, payload);
            setSegmentDialog(null);
            enqueueSnackbar(
                editing
                    ? intl.formatMessage({ id: "impact.segment_updated", defaultMessage: "Segment saved" })
                    : intl.formatMessage({ id: "impact.segment_created", defaultMessage: "Segment created" }),
                { variant: "success" }
            );
            // Refetched, not patched: removing a choice clears it from impacts,
            // so the rows move as well as the bar.
            fetchAll();
        } catch (error) {
            console.error("Failed to save the segment:", error);
            setSegmentError(segmentErrorFrom(error, intl));
        } finally {
            setSegmentSaving(false);
        }
    };

    const handleDeleteSegment = async () => {
        const segment = pendingSegmentDelete;
        if (!segment) return;
        try {
            setDeleting(true);
            await registerApi.deleteSegment(projectId, segment.id);
            setPendingSegmentDelete(null);
            enqueueSnackbar(
                intl.formatMessage({ id: "impact.segment_deleted", defaultMessage: "Segment deleted" }),
                { variant: "success" }
            );
            fetchAll();
        } catch (error) {
            console.error("Failed to delete the segment:", error);
            enqueueSnackbar(
                intl.formatMessage({
                    id: "impact.segment_delete_failed",
                    defaultMessage: "Failed to delete the segment",
                }),
                { variant: "error" }
            );
        } finally {
            setDeleting(false);
        }
    };

    // choice id -> impacts holding it, for the segment being edited — so the
    // dialog can say what removing a choice costs.
    const segmentUsage = useMemo(
        () => (segmentDialog?.segment ? countsFor(impacts, segmentDialog.segment.id) : {}),
        [impacts, segmentDialog]
    );
    // Impacts that lose a value if the pending segment goes.
    const segmentDeleteCount = useMemo(
        () =>
            pendingSegmentDelete
                ? impacts.filter((impact) => valueOf(impact, pendingSegmentDelete.id) != null).length
                : 0,
        [impacts, pendingSegmentDelete]
    );

    // ── Deletes ─────────────────────────────────────────────────────────────
    const handleDeleteImpact = async () => {
        if (!pendingImpactDelete) return;
        try {
            setDeleting(true);
            await registerApi.deleteImpact(projectId, pendingImpactDelete.id);
            setImpacts((prev) => prev.filter((row) => String(row.id) !== String(pendingImpactDelete.id)));
            setPendingImpactDelete(null);
            enqueueSnackbar(intl.formatMessage({ id: "impact.deleted", defaultMessage: "Impact deleted" }), {
                variant: "success",
            });
        } catch (error) {
            console.error("Failed to delete impact:", error);
            enqueueSnackbar(
                intl.formatMessage({ id: "impact.delete_failed", defaultMessage: "Failed to delete impact" }),
                { variant: "error" }
            );
        } finally {
            setDeleting(false);
        }
    };

    // A new impact starts at Any in every segment, whatever is filtered: Any
    // already shows under the current filter, and narrowing it is a choice.
    const openNewImpact = () => setImpactDrawer({});

    // The selection heading names the filter in force: every chosen value, in
    // segment order, or "All impacts".
    const selectionLabel = hasFilters
        ? segments
              .filter((segment) => filters[String(segment.id)])
              .map((segment) => choiceOf(segment, filters[String(segment.id)])?.label)
              .join(" · ")
        : null;

    const selectionHeader = (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                mb: 1.5,
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectionLabel || (
                        <FormattedMessage id="impact.rail_all_impacts" defaultMessage="All impacts" />
                    )}
                </Typography>
                {hasFilters && (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        <FormattedMessage
                            id="impact.filtered_count"
                            defaultMessage="{shown, number} of {total, number} impacts"
                            values={{ shown: visibleImpacts.length, total: impacts.length }}
                        />
                    </Typography>
                )}
            </Box>

            <Stack direction="row" spacing={1.5} alignItems="center">
                {!canEdit && (
                    <Chip
                        size="small"
                        variant="outlined"
                        label={intl.formatMessage({ id: "projects.view_only", defaultMessage: "View only" })}
                    />
                )}
                {impacts.length > 0 && (
                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadOutlinedIcon />}
                        onClick={handleExport}
                        sx={{ color: "#00438E", borderColor: "#00438E" }}
                    >
                        <FormattedMessage id="impact.download_excel" defaultMessage="Download to Excel" />
                    </Button>
                )}
                {canEdit && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openNewImpact}
                        sx={{ backgroundColor: "#00438E", "&:hover": { backgroundColor: "#003A75" } }}
                    >
                        <FormattedMessage id="impact.add_impact" defaultMessage="Add impact" />
                    </Button>
                )}
            </Stack>
        </Box>
    );

    return (
        <KeepScrollPosition>
        <Box>
            <Box
                sx={{
                    mb: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 2,
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        <FormattedMessage
                            id="impact.workspace_summary_segments"
                            defaultMessage="{segments, number} {segments, plural, one {segment} other {segments}} · {impacts, number} {impacts, plural, one {impact} other {impacts}}"
                            values={{ segments: segments.length, impacts: impacts.length }}
                        />
                    </Typography>
                </Box>

                {/* A segmented control, not a tab strip: register and dashboard are
                    two ways of looking at one thing, not a level of hierarchy. */}
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={view}
                    onChange={(_e, next) => next && selectView(next)}
                    aria-label={intl.formatMessage({ id: "impact.views", defaultMessage: "Views" })}
                    sx={{
                        flexShrink: 0,
                        backgroundColor: "rgba(0,0,0,0.04)",
                        borderRadius: 999,
                        p: 0.5,
                        "& .MuiToggleButtonGroup-grouped": {
                            border: 0,
                            borderRadius: "999px !important",
                            textTransform: "none",
                            fontWeight: 600,
                            px: 2,
                            py: 0.75,
                            color: "text.secondary",
                            "&.Mui-selected": {
                                backgroundColor: "#fff",
                                color: "#00438E",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.16)",
                                "&:hover": { backgroundColor: "#fff" },
                            },
                        },
                    }}
                >
                    {WORKSPACE_VIEWS.map((option) => (
                        <ToggleButton key={option.key} value={option.key}>
                            {intl.formatMessage({ id: option.labelId, defaultMessage: option.label })}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            {/* The spinner is for the FIRST load only. A refresh after a save or a
                delete keeps the page on screen and swaps the data in place:
                replacing the whole register with a spinner collapses the page,
                and the browser throws the scroll position back to the top. */}
            {loading && !loaded ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress sx={{ color: "#00438E" }} />
                </Box>
            ) : view === ACTIVITY_VIEW ? (
                <ActivityDashboard
                    projectId={projectId}
                    canEdit={canEdit}
                    dashboard={dashboard}
                    loading={dashboardLoading && !dashboard}
                    error={dashboardError && !dashboard}
                    onRefresh={fetchDashboard}
                    onOpenImpact={openActivitiesFor}
                    segments={segments}
                    registerImpacts={impacts}
                />
            ) : (
                <>
                    {/* The segment bar renders always, empty included: an empty
                        section is the section saying it is empty, with the
                        control that fills it still in place. The one exception
                        is a failed load — an empty bar offering "Add segment"
                        would claim there are none. */}
                    {segmentsFailed ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <FormattedMessage
                                id="impact.segments_unavailable"
                                defaultMessage="Segments couldn't be loaded, so filtering by segment is unavailable. Impacts are shown unfiltered, and saving one leaves its segments as they were."
                            />
                        </Alert>
                    ) : (
                    <SegmentFilters
                        segments={segments}
                        impacts={impacts}
                        filters={filters}
                        onChangeFilter={setFilter}
                        onClearFilters={clearFilters}
                        onAddSegment={() => {
                            setSegmentError(null);
                            setSegmentDialog({});
                        }}
                        onEditSegment={(segment) => {
                            setSegmentError(null);
                            setSegmentDialog({ segment });
                        }}
                        onDeleteSegment={(segment) => setPendingSegmentDelete(segment)}
                        readOnly={!canEdit}
                    />
                    )}

                    {selectionHeader}

                    {visibleImpacts.length === 0 ? (
                        <Paper
                            variant="outlined"
                            sx={{ p: 6, textAlign: "center", color: "text.secondary", borderRadius: 2 }}
                        >
                            <Typography variant="body2" sx={{ mb: hasFilters ? 0 : 2 }}>
                                {hasFilters ? (
                                    <FormattedMessage
                                        id="impact.no_impacts_match"
                                        defaultMessage="No impacts match these filters."
                                    />
                                ) : (
                                    <FormattedMessage id="impact.no_impacts_yet" defaultMessage="No impacts logged yet" />
                                )}
                            </Typography>
                            {canEdit && !hasFilters && (
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={openNewImpact}
                                    sx={{ color: "#00438E", borderColor: "#00438E" }}
                                >
                                    <FormattedMessage id="impact.add_impact" defaultMessage="Add impact" />
                                </Button>
                            )}
                        </Paper>
                    ) : (
                        <ImpactsTable
                            impacts={visibleImpacts}
                            segments={segments}
                            onEdit={canEdit ? (impact) => setImpactDrawer({ impact }) : undefined}
                            onDelete={canEdit ? (impact) => setPendingImpactDelete(impact) : undefined}
                            onActivities={(impact) => setDetailDrawer({ impact })}
                        />
                    )}
                </>
            )}

            {/* ── Impact drawer ── */}
            <Drawer anchor="right" open={Boolean(impactDrawer)} onClose={() => setImpactDrawer(null)} sx={DRAWER_SX}>
                <Box sx={{ p: 2.5 }}>
                    <DrawerHeader
                        title={
                            impactDrawer?.impact ? (
                                <FormattedMessage id="impact.edit_impact" defaultMessage="Edit impact" />
                            ) : (
                                <FormattedMessage id="impact.new_impact" defaultMessage="New impact" />
                            )
                        }
                        onClose={() => setImpactDrawer(null)}
                        closeLabel={intl.formatMessage({ id: "general.close", defaultMessage: "Close" })}
                    />
                    {impactDrawer && (
                        <ImpactForm
                            // Remount per open so a fresh form never inherits the last one's state.
                            key={impactDrawer.impact?.id ?? "new"}
                            projectId={projectId}
                            impactId={impactDrawer.impact?.id}
                            impact={impactDrawer.impact}
                            segments={segments}
                            segmentsAvailable={!segmentsFailed}
                            onSaved={() => {
                                setImpactDrawer(null);
                                fetchAll();
                                // The tracker lists every impact, so a new one has
                                // to appear there too.
                                fetchDashboard();
                            }}
                            onCancel={() => setImpactDrawer(null)}
                        />
                    )}
                </Box>
            </Drawer>

            {/* ── Segment dialog: create, or edit one ── */}
            <SegmentForm
                open={Boolean(segmentDialog)}
                segment={segmentDialog?.segment || null}
                usage={segmentUsage}
                saving={segmentSaving}
                error={segmentError}
                onSubmit={handleSaveSegment}
                onCancel={() => {
                    setSegmentDialog(null);
                    setSegmentError(null);
                }}
            />

            {/* ── Delete a segment — says the impacts stay, only the value goes ── */}
            <Dialog
                open={Boolean(pendingSegmentDelete)}
                onClose={() => (deleting ? null : setPendingSegmentDelete(null))}
            >
                <DialogTitle>
                    <FormattedMessage id="impact.delete_segment" defaultMessage="Delete segment" />
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <FormattedMessage
                            id="impact.delete_segment_confirm"
                            defaultMessage="Delete “{segment}” and its choices? {count, plural, =0 {No impact uses it.} one {# impact loses its {segment} value.} other {# impacts lose their {segment} value.}} The impacts themselves stay."
                            values={{ segment: pendingSegmentDelete?.title || "", count: segmentDeleteCount }}
                        />
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setPendingSegmentDelete(null)}
                        disabled={deleting}
                        sx={{ color: "text.secondary" }}
                    >
                        <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
                    </Button>
                    <Button onClick={handleDeleteSegment} disabled={deleting} color="error" variant="contained">
                        <FormattedMessage id="general.delete" defaultMessage="Delete" />
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Row detail: activities ── */}
            <Drawer
                anchor="right"
                open={Boolean(detailDrawer)}
                onClose={() => setDetailDrawer(null)}
                sx={DRAWER_SX}
            >
                <Box sx={{ p: 2.5 }}>
                    <DrawerHeader
                        title={<FormattedMessage id="impact.detail_title" defaultMessage="Impact" />}
                        onClose={() => setDetailDrawer(null)}
                        closeLabel={intl.formatMessage({ id: "general.close", defaultMessage: "Close" })}
                    />
                    {detailDrawer && (
                        // Remount per row so one impact's activities can never
                        // continue into another's.
                        <ImpactDetailPanel
                            key={detailDrawer.impact.id}
                            projectId={projectId}
                            impact={detailDrawer.impact}
                            segments={segments}
                                    onActivitiesChanged={handleActivitiesChanged}
                            canEdit={canEdit}
                        />
                    )}
                </Box>
            </Drawer>

            {/* ── Impact delete confirm ── */}
            <Dialog
                open={Boolean(pendingImpactDelete)}
                onClose={() => (deleting ? null : setPendingImpactDelete(null))}
            >
                <DialogTitle>
                    <FormattedMessage id="impact.delete_title" defaultMessage="Delete impact" />
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <FormattedMessage
                            id="impact.delete_confirm"
                            defaultMessage="Delete “{what}”? This can't be undone."
                            values={{ what: pendingImpactDelete?.what_is_changing || "" }}
                        />
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingImpactDelete(null)} disabled={deleting} sx={{ color: "text.secondary" }}>
                        <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
                    </Button>
                    <Button onClick={handleDeleteImpact} disabled={deleting} color="error" variant="contained">
                        <FormattedMessage id="general.delete" defaultMessage="Delete" />
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
        </KeepScrollPosition>
    );
}

function DrawerHeader({ title, onClose, closeLabel }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#00438E" }}>
                {title}
            </Typography>
            <IconButton onClick={onClose} aria-label={closeLabel}>
                <CloseIcon />
            </IconButton>
        </Box>
    );
}
